import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaEye, FaSave, FaTrash } from "react-icons/fa";
import "../../css/others/Table.css";
import { apiCall } from "../../main/ApiCall";
import ToastPopup from "../../popups/ToastPopup";
import { Question, Attachment, ExecutionChecklistDetails, TypeEnum, createToastMessage, ToastMessage } from '../../interFaces/interfaces';
import EncryptionUtils from "../../main/encryptionUtils";
const AuditorChecklistDetails: React.FC = () => {
  const { campaignId, projectId } = useParams<{ campaignId: string; projectId: string }>();
  const [details, setDetails] = useState<ExecutionChecklistDetails>({ content: [], totalElements: 0, totalPages: 0 });
  const [, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [isAttachModalOpen, setIsAttachModalOpen] = useState<boolean>(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([]);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [typeEnums, setTypeEnums] = useState<TypeEnum[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{ message: string; onConfirm: () => void; onCancel?: () => void; }>({ message: "", onConfirm: () => { } });
  const [allFieldsFilled, setAllFieldsFilled] = useState<boolean>(false);
  const [tableHeaders, setTableHeaders] = useState<string[]>([]);
  const [isSavingSingle, setIsSavingSingle] = useState<boolean>(false);
  const [isFindingsModalOpen, setIsFindingsModalOpen] = useState(false);
  const [currentFindingIndex, setCurrentFindingIndex] = useState<number | null>(null);
  const [findingText, setFindingText] = useState<string>("");
  const userInfo = EncryptionUtils.get<any>("userInfo", null);
  const roleName = EncryptionUtils.get("roleName");
  const userId = userInfo?.id;
  const createdBy = userInfo?.email;
  const navigate = useNavigate();
  useEffect(() => {
    const fetchTypeEnums = async () => {
      try {
        const response = await apiCall("get", "/api/enums/TypeEnum");
        setTypeEnums(response);
      } catch (error) {
        console.error("Error fetching TypeEnum values:", error);
      }
    };
    fetchTypeEnums();
  }, []);
  const checkAllFieldsFilled = useCallback(() => {
    if (!details || !details.content || details.content.length === 0) return false;
    return details.content.every(item => item.questionText?.trim() && item.typeOfFindings?.trim() && item.findings?.trim());
  }, [details]);
  useEffect(() => {
    setAllFieldsFilled(checkAllFieldsFilled());
  }, [details, checkAllFieldsFilled]);
  const showConfirmModal = (message: string, onConfirm: () => void, onCancel?: () => void) => {
    setConfirmModalConfig({ message, onConfirm, onCancel });
    setIsConfirmModalOpen(true);
  };
  const fetchChecklistDetails = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const requestBody = { campaignId, projectId, createdBy, ...(roleName === "Auditor" && { auditorId: userId }) };
      const response = await apiCall("post", `/api/audit/campaign/auditor/questions`, requestBody, { page: currentPage - 1, size: itemsPerPage });
      if (!response || !response.responses) {
        setDetails({ content: [], totalElements: 0, totalPages: 0 });
        return;
      }
      const availableHeaders = [];
      if (response.responses.some((item: { practiceArea: any; }) => item.practiceArea)) availableHeaders.push("practiceArea");
      if (response.responses.some((item: { pims: any; }) => item.pims)) availableHeaders.push("pims");
      if (response.responses.some((item: { isms: any; }) => item.isms)) availableHeaders.push("isms");
      if (response.responses.some((item: { bcms: any; }) => item.bcms)) availableHeaders.push("bcms");
      if (response.responses.some((item: { cmmi: any; }) => item.cmmi)) availableHeaders.push("cmmi");
      if (response.responses.some((item: { soc1: any; }) => item.soc1)) availableHeaders.push("soc1");
      if (response.responses.some((item: { soc2: any; }) => item.soc2)) availableHeaders.push("soc2");
      if (response.responses.some((item: { qms: any; }) => item.qms)) availableHeaders.push("qms");
      if (response.responses.some((item: { aims: any; }) => item.aims)) availableHeaders.push("aims");
      if (response.responses.some((item: { erm: any; }) => item.erm)) availableHeaders.push("erm");
      if (response.responses.some((item: { itsm: any; }) => item.itsm)) availableHeaders.push("itsm");
      if (response.responses.some((item: { ers: any; }) => item.ers)) availableHeaders.push("ers");
      if (response.responses.some((item: { ohs: any; }) => item.ohs)) availableHeaders.push("ohs");
      setTableHeaders(availableHeaders);
      const auditId = response.auditId;
      const mappedResponse: ExecutionChecklistDetails = { content: response.responses.map((item: any) => ({ questionId: item.questionId, questionText: item.questionText || "Empty", practiceArea: item.practiceArea || "", typeOfFindings: item.typeOfFindings || "", findings: item.findings || "", attachments: item.attachments || [], auditId: auditId, pims: item.pims || "", isms: item.isms || "", bcms: item.bcms || "", cmmi: item.cmmi || "", soc1: item.soc1 || "", soc2: item.soc2 || "", qms: item.qms || "", aims: item.aims || "", erm: item.erm || "", itsm: item.itsm || "", ers: item.ers || "", ohs: item.ohs || "" })), totalElements: response.totalElements || 0, totalPages: response.totalPages || 0 };
      setDetails(mappedResponse);
    } catch (error) {
      console.error("Error fetching checklist details:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, campaignId, projectId, createdBy, roleName, currentPage, itemsPerPage]);
  useEffect(() => {
    fetchChecklistDetails();
  }, [fetchChecklistDetails]);
  const handlePageChange = (newPage: number) => {
    if (hasUnsavedChanges) {
      showConfirmModal("You have unsaved changes. Are you sure you want to go to the next page?", () => setCurrentPage(newPage));
      return;
    }
    setCurrentPage(newPage);
  };
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = "You have unsaved changes. Do you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
  const handleInputChange = (index: number, key: keyof Question, value: any) => {
    const updatedDetails = { ...details };
    if (key === "attachments") {
      if (Array.isArray(value)) {
        updatedDetails.content[index][key] = value as File[];
      } else {
        setMessage(createToastMessage("Invalid value for attachments. Expected an array.", "warning", 3000));
        return;
      }
    } else {
      updatedDetails.content[index][key] = value as any;
    }
    setDetails(updatedDetails);
    setHasUnsavedChanges(true);
  };
  const handleNavigation = () => {
    if (hasUnsavedChanges) {
      showConfirmModal("You have unsaved changes. Are you sure you want to leave?", () => window.history.back());
    } else {
      window.history.back();
    }
  };
  const handleAuditorSave = async (questionId: string) => {
    setIsSavingSingle(true);
    const questionToSave = details.content.find((item) => item.questionId === questionId);
    if (!questionToSave) {
      setMessage(createToastMessage("Question not found.", "warning", 3000));
      setIsSavingSingle(false);
      return;
    }
    if (!questionToSave.typeOfFindings?.trim()) {
      setMessage(createToastMessage("Please select a Type of Findings before saving.", "warning", 3000));
      setIsSavingSingle(false);
      return;
    }
    if (!questionToSave.findings?.trim()) {
      setMessage(createToastMessage("Please enter Findings before saving.", "warning", 1500));
      setIsSavingSingle(false);
      return;
    }
    const attachments = questionToSave.attachments.map((file) => {
      return file instanceof File ? { fileKey: file.name } : { fileKey: file.fileKey };
    });
    const { auditId } = questionToSave;
    if (!auditId) {
      setMessage(createToastMessage("Audit ID is missing.", "warning", 3000));
      setIsSavingSingle(false);
      return;
    }
    const payload = [{ questionId: questionToSave.questionId, questionText: questionToSave.questionText, practiceArea: questionToSave.practiceArea, typeOfFindings: questionToSave.typeOfFindings, findings: questionToSave.findings, attachments, pims: questionToSave.pims || "", isms: questionToSave.isms || "", bcms: questionToSave.bcms || "", cmmi: questionToSave.cmmi || "", soc1: questionToSave.soc1 || "", soc2: questionToSave.soc2 || "", qms: questionToSave.qms || "", aims: questionToSave.aims || "", erm: questionToSave.erm || "", itsm: questionToSave.itsm || "", ers: questionToSave.ers || "", ohs: questionToSave.ohs || "" }];
    try {
      const response = await apiCall("put", `/api/audit/${auditId}/responses`, payload);
      const responseAttachments = response?.[0]?.attachments || [];
      const filesToUpload = questionToSave.attachments.filter(file => file instanceof File) as File[];
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const matched = responseAttachments.find(
          (att: any) => att.fileKey === file.name
        );
        const presignedUrl = matched?.presignedUrl;
        if (!presignedUrl) {
          console.warn(`No pre-signed URL found for file: ${file.name}`);
          continue;
        }
        try {
          await axios.put(presignedUrl, file, { headers: { "Content-Type": file.type } });
          console.log(`Uploaded: ${file.name}`);
        } catch (uploadError) {
          console.error(`Error uploading file: ${file.name}`, uploadError);
        }
      }
      setDetails(prevDetails => ({ ...prevDetails, content: prevDetails.content.map(item => item.questionId === questionId ? { ...item, attachments: item.attachments.map(att => att instanceof File ? { fileKey: att.name, uploaded: true } : att) } : item) }));
      setMessage(createToastMessage("Question saved successfully!", "success", 3000));
      setHasUnsavedChanges(false);
    } catch (error) {
      setMessage(createToastMessage("Failed to save question responses.", "error", 3000));
    } finally {
      setIsSavingSingle(false);
    }
  };
  const handleSelectAll = () => {
    if (selectedQuestions.length === details.content.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(details.content.map((item) => item.questionId));
    }
  };
  const handleSaveAll = async () => {
    if (selectedQuestions.length === 0) {
      setMessage(createToastMessage("Please select at least one question to save.", "warning", 3000));
      return;
    }
    const invalidQuestions = selectedQuestions.filter(questionId => {
      const question = details.content.find(q => q.questionId === questionId);
      return !question || !question.questionText?.trim() || !question.typeOfFindings?.trim() || !question.findings?.trim();
    });
    if (invalidQuestions.length > 0) {
      setMessage(createToastMessage("Please fill all required details for selected questions.", "warning", 3000));
      return;
    }
    const auditId = details.content[0]?.auditId;
    if (!auditId) {
      setMessage(createToastMessage("Audit ID is missing.", "warning", 3000));
      return;
    }
    try {
      const payload = selectedQuestions
        .map((questionId) => {
          const q = details.content.find(item => item.questionId === questionId);
          if (!q) return null;
          const attachments = (q.attachments || []).map(file =>
            file instanceof File ? { fileKey: file.name } : { fileKey: file.fileKey }
          );
          return {
            questionId: q.questionId, questionText: q.questionText, practiceArea: q.practiceArea, typeOfFindings: q.typeOfFindings, findings: q.findings || "", attachments, pims: q.pims || "", isms: q.isms || "", bcms: q.bcms || "", cmmi: q.cmmi || "", soc1: q.soc1 || "", soc2: q.soc2 || "", qms: q.qms || "", aims: q.aims || "", erm: q.erm || "", itsm: q.itsm || "", ers: q.ers || "", ohs: q.ohs || ""
          };
        })
        .filter(Boolean);
      const response = await apiCall("put", `/api/audit/${auditId}/responses`, payload);
      for (const questionResponse of response) {
        const questionId = questionResponse.questionId;
        const responseAttachments = questionResponse.attachments || [];
        const localQuestion = details.content.find(q => q.questionId === questionId);
        if (!localQuestion) continue;
        const filesToUpload = (localQuestion.attachments || []).filter(
          f => f instanceof File
        ) as File[];
        for (const file of filesToUpload) {
          const matched = responseAttachments.find(
            (att: any) => att.fileKey === file.name
          );
          const presignedUrl = matched?.presignedUrl;
          if (!presignedUrl) {
            console.warn(`No presigned URL for ${file.name}`);
            continue;
          }
          try {
            await axios.put(presignedUrl, file, { headers: { "Content-Type": file.type }, });
          } catch (err) {
            console.error(`Upload failed for ${file.name}`, err);
          }
        }
      }
      setDetails(prev => ({
        ...prev, content: prev.content.map(item => {
          if (selectedQuestions.includes(item.questionId)) {
            return {
              ...item, attachments: item.attachments.map(att => att instanceof File ? { fileKey: att.name, uploaded: true } : att
              )
            };
          }
          return item;
        })
      }));
      setMessage(createToastMessage("All selected questions saved successfully!", "success", 3000));
      setHasUnsavedChanges(false);
      setSelectedQuestions([]);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to save questions";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const handleSelectQuestion = (questionId: string) => {
    setSelectedQuestions((prevSelected) =>
      prevSelected.includes(questionId) ? prevSelected.filter((id) => id !== questionId) : [...prevSelected, questionId]
    );
  };
  const handleBulkFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setBulkFile(file);
    }
  };
  const handleSaveBulkAttachments = async () => {
    if (!bulkFile || selectedQuestions.length === 0) {
      setMessage(createToastMessage("No file selected or no questions selected", "warning", 3000));
      return;
    }
    const allHaveType = selectedQuestions.every(
      (questionId) => details.content.find((q) => q.questionId === questionId)?.typeOfFindings
    );
    if (!allHaveType) {
      setMessage(createToastMessage("All selected questions must have a Type before attaching a file.", "warning", 3000));
      return;
    }
    const updatedDetails = { ...details };
    selectedQuestions.forEach((questionId) => {
      const question = updatedDetails.content.find((q) => q.questionId === questionId);
      if (question) {
        if (!question.attachments) question.attachments = [];
        question.attachments.push(bulkFile);
      }
    });
    setDetails(updatedDetails);
    setIsAttachModalOpen(false);
    setMessage(createToastMessage("Bulk file attachments saved successfully!", "success", 3000));
    setHasUnsavedChanges(true);
  };
  const handleSubmitForAuditClose = async () => {
    showConfirmModal("Are you sure you want to submit this checklist for audit close?",
      async () => {
        try {
          const auditId = details.content[0]?.auditId;
          if (!auditId) {
            setMessage(createToastMessage("Audit ID is missing. Cannot submit for audit closure.", "warning", 3000));
            return;
          }
          let allQuestions: Question[] = [];
          for (let page = 0; page < details.totalPages; page++) {
            const requestBody = { campaignId, auditorId: userId, projectId, createdBy };
            const response = await apiCall("post", `/api/audit/campaign/auditor/questions`, requestBody, { page, size: itemsPerPage });
            const pageQuestions = response?.responses?.map((item: any) => ({ questionId: item.questionId, questionText: item.questionText || "", practiceArea: item.practiceArea || "", typeOfFindings: item.typeOfFindings || "", findings: item.findings || "", attachments: item.attachments || [] })) || [];
            allQuestions = [...allQuestions, ...pageQuestions];
          }
          const hasIncompleteFields = allQuestions.some(
            (item) => !item?.questionText?.trim() || !item?.typeOfFindings?.trim() || !item?.findings?.trim()
          );
          if (hasIncompleteFields) {
            setMessage(createToastMessage("Please fill all required details before submitting.", "warning", 3000));
            return;
          }
          try {
            await apiCall("put", `/api/audit/${auditId}`);
            setMessage(createToastMessage("Audit Closed successfully!", "success", 3000));
            setTimeout(() => { navigate(-1); }, 3000);
          } catch (error: any) {
            const errorMessage = error?.response?.data?.message;
            setMessage(createToastMessage(errorMessage, "error", 3000));
          }
        } catch (error) {
          setMessage(createToastMessage("Failed to complete audit close process. Please try again.", "error", 3000));
        }
      }
    );
  };
  const getPageNumbers = (): number[] => {
    const total = details.totalPages;
    if (total <= 3) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (currentPage === 1) {
      return [1, 2, 3];
    } else if (currentPage === total) {
      return [total - 2, total - 1, total];
    } else {
      return [currentPage - 1, currentPage, currentPage + 1];
    }
  };
  const getHeaderDisplayName = (header: string): string => {
    const headerMap: Record<string, string> = { practiceArea: "Control/Practice Area", pims: "PIMS", isms: "ISMS", bcms: "BCMS", cmmi: "CMMI", soc1: "SOC1", soc2: "SOC2", qms: "QMS", aims: "AIMS", erm: "ERM", itsm: "ITSM", ers: "ERS", ohs: "OHS" };
    return headerMap[header] || header;
  };
  const handleDeleteAttachment = (index: number) => {
    if (selectedQuestionIndex === null) return;
    const updatedDetails = { ...details };
    updatedDetails.content[selectedQuestionIndex].attachments.splice(index, 1);
    setDetails(updatedDetails);
    setSelectedAttachments(
      updatedDetails.content[selectedQuestionIndex].attachments.filter(
        (att): att is Attachment => "fileKey" in att
      )
    );
    setHasUnsavedChanges(true);
  };
  const openFindingsModal = (index: number) => {
    setCurrentFindingIndex(index);
    setFindingText(details.content[index].findings || "");
    setIsFindingsModalOpen(true);
  };
  const saveFindingsFromModal = () => {
    if (currentFindingIndex === null) return;
    if (!validateNoRestrictedChars(findingText)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedCharsRegex}`, "warning", 3000));
      return;
    }
    const updatedDetails = { ...details };
    updatedDetails.content[currentFindingIndex].findings = findingText;
    setDetails(updatedDetails);
    setHasUnsavedChanges(true);
    setIsFindingsModalOpen(false);
  };
  const restrictedCharsRegex = /[\\:*?"<>|/]/;
  const validateNoRestrictedChars = (value: string) => {
    return !restrictedCharsRegex.test(value);
  };
  const handleRestrictedInput = (value: string, updateFn: () => void) => {
    if (!validateNoRestrictedChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedCharsRegex}`, "warning", 3000));
      return;
    }
    updateFn();
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Audit Details</h2>
        <div className="table-title-actions">
          {selectedQuestions.length > 1 && (
            <button className="add-question-button" onClick={() => setIsAttachModalOpen(true)}> Attach File to Selected  </button>
          )}
          {currentPage === details.totalPages && (
            <button className={`submit-audit-button ${!allFieldsFilled || hasUnsavedChanges ? 'disabled' : ''}`} onClick={handleSubmitForAuditClose} disabled={!allFieldsFilled || hasUnsavedChanges} title={!allFieldsFilled ? "Please fill all required fields" : hasUnsavedChanges ? "Please save your changes first" : ""}> Submit for Audit Close</button>
          )}
          <button className="save-all-button" onClick={handleSaveAll} disabled={selectedQuestions.length === 0} title={selectedQuestions.length === 0 ? "Please select at least one question to save" : ""} > Save All</button>
        </div>
      </div>
      <div className="table-container">
        <div className="table">
          <table className="main-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" onChange={handleSelectAll} checked={selectedQuestions.length === details.content.length && details.content.length > 0} />
                </th>
                <th>S.No.</th>
                <th className="header-width">Question</th>
                {tableHeaders.map(header => (
                  <th key={header} className="header-width">{getHeaderDisplayName(header)}</th>
                ))}
                <th className="header-width">Type of Findings</th>
                <th className="header-width">Findings</th>
                <th className="header-width">Attachments</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {details.content.length > 0 ? (
                details.content.map((item, index) => (
                  <tr key={item.questionId}>
                    <td>
                      <input type="checkbox" checked={selectedQuestions.includes(item.questionId)} onChange={() => handleSelectQuestion(item.questionId)} />
                    </td>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{item.questionText || "Empty"}</td>
                    {tableHeaders.map(header => (
                      <td key={header}>{item[header as keyof Question] || "Empty"}</td>
                    ))}
                    <td>
                      <select value={item.typeOfFindings || ""} onChange={(e) => handleInputChange(index, "typeOfFindings", e.target.value)} disabled={isSavingSingle} >
                        <option value="">Select Type</option>
                        {typeEnums.map((enumItem) => (
                          <option key={enumItem.value} value={enumItem.value}> {enumItem.description} </option>
                        ))}
                      </select>
                    </td>
                    <td className="findings-cell">
                      {item.typeOfFindings ? (
                        <input type="text" value={item.findings || ""} placeholder="Click to enter findings" className={!item.findings ? "missing-field" : ""} onClick={() => openFindingsModal(index)} />
                      ) : (
                        <span className="not-applicable-text">Not Applicable</span>
                      )}
                    </td>
                    <td>
                      {item.typeOfFindings ? (
                        <>
                          <input type="file" multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              const updatedDetails = { ...details };
                              if (!updatedDetails.content[index].attachments) {
                                updatedDetails.content[index].attachments = [];
                              }
                              updatedDetails.content[index].attachments.push(...files);
                              setDetails(updatedDetails);
                              setHasUnsavedChanges(true);
                            }}
                            disabled={isSavingSingle}
                          />
                          <FaEye className="action-button" onClick={() => { setSelectedAttachments(item.attachments.filter((att): att is Attachment => "fileKey" in att)); setSelectedQuestionIndex(index); setIsViewModalOpen(true) }} />
                        </>
                      ) : (
                        <span className="not-applicable-text">Not Applicable</span>
                      )}
                    </td>
                    <td>
                      <FaSave className={`action-button ${isSavingSingle ? 'saving' : ''}`} onClick={() => handleAuditorSave(item.questionId)} title="Save" />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8 + tableHeaders.length} className="no-data"> No questions available for this project </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pagination">
        <button className="back-btn" onClick={handleNavigation}>Back</button>
        <button className="info">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, details.totalElements)} of{" "}
          {details.totalElements} items
        </button>
        <div className="pagination-controls">
          <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <span className="small-pipe">|</span>&#8249;
          </button>
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>&#8249;</button>
          {getPageNumbers().map((page) => (
            <button key={page} onClick={() => handlePageChange(page)} className={`pagination-btn ${currentPage === page ? "active" : ""}`}> {page} </button>
          ))}
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === details.totalPages}>&#8250;</button>
          <button className="pagination-btn" onClick={() => handlePageChange(details.totalPages)} disabled={currentPage === details.totalPages}> &#8250;<span className="small-pipe">|</span> </button>
        </div>
        <div>
          <select id="itemsPerPageSelect" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1) }} className="items-perpage-select">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
      {isAttachModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Bulk File Upload</h2>
            <input type="file" onChange={handleBulkFileUpload} accept=".csv,.xls,.xlsx" />
            <div className="buttons">
              <button className="savebutton" onClick={handleSaveBulkAttachments}>Save</button>
              <button className="cancelbutton" onClick={() => setIsAttachModalOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {isViewModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Attachments</h2>
            <ul>
              {selectedAttachments.map((attachment, i) => (
                <li key={i}>
                  <span>{attachment.fileKey}</span>
                  <button type="button" className="action-button" onClick={() => handleDeleteAttachment(i)} ><FaTrash /></button>
                </li>
              ))}
            </ul>
            <button className="close" onClick={() => setIsViewModalOpen(false)}>Close</button>
          </div>
        </div>
      )}
      {isConfirmModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Confirmation</h2>
            <p>{confirmModalConfig.message}</p>
            <div className="buttons">
              <button className="savebutton" onClick={() => { confirmModalConfig.onConfirm(); setIsConfirmModalOpen(false); }}>Confirm</button>
              <button className="cancelbutton" onClick={() => { confirmModalConfig.onCancel?.(); setIsConfirmModalOpen(false); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {isFindingsModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Enter Findings</h2>
            <textarea value={findingText} onChange={(e) => handleRestrictedInput(e.target.value, () => setFindingText(e.target.value))} rows={6} placeholder="Enter findings here..." />
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setIsFindingsModalOpen(false)}>Cancel</button>
              <button className="savebutton" onClick={saveFindingsFromModal}>Ok</button>
            </div>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default AuditorChecklistDetails;